/**
 * RecommendationList v2.0 - FAIR Implementation Planner
 *
 * EXACT implementation from Recommendations_UX_Design_v2.0_FINAL.md
 * Section 3.1: Three-Panel Layout (lines 569-600)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiClient } from '../hooks/useApiClient';
import '../styles/journey.css';

// Types matching design doc
type RecommendationCategory = 'critical_issue' | 'optimization_opportunity' | 'best_practice' | 'future_enhancement';
type RecommendationStatus = 'new' | 'reviewing' | 'approved' | 'rejected' | 'postponed' | 'in_progress' | 'implemented' | 'verified';
type Priority = 'critical' | 'high' | 'medium' | 'low';
type EffortLevel = 'low' | 'medium' | 'high';

interface Recommendation {
  id: string;
  category: RecommendationCategory;
  priority: Priority;
  title: string;
  description: string;
  fair_pillars: ('F' | 'A' | 'I' | 'R')[];
  impact_score: number;
  effort_level: EffortLevel;
  estimated_cost_min?: number;
  estimated_cost_max?: number;
  station_id: string;
  status: RecommendationStatus;
  evidence?: Array<{
    source_name: string;
    excerpt: string;
    location: string;
  }>;
}

const CATEGORY_LABELS: Record<RecommendationCategory, string> = {
  critical_issue: 'Critical issue',
  optimization_opportunity: 'Optimization opportunity',
  best_practice: 'Best practice',
  future_enhancement: 'Future enhancement',
};

interface RecommendationListProps {
  groupId: string;
}

export const RecommendationList: React.FC<RecommendationListProps> = ({ groupId }) => {
  const navigate = useNavigate();
  const { authFetch } = useApiClient();
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Filter states (per design doc Section 4.2)
  const [filterCategory, setFilterCategory] = useState<{
    critical: boolean;
    optimize: boolean;
    best_practice: boolean;
    future: boolean;
  }>({ critical: true, optimize: true, best_practice: false, future: false });

  const [filterPriority, setFilterPriority] = useState<{
    critical: boolean;
    high: boolean;
    medium: boolean;
    low: boolean;
  }>({ critical: true, high: true, medium: false, low: false });

  const [filterStatus, setFilterStatus] = useState<{
    new: boolean;
    approved: boolean;
    implemented: boolean;
  }>({ new: true, approved: false, implemented: false });

  const [filterStation, setFilterStation] = useState<{
    docs: boolean;
    interview: boolean;
    live: boolean;
  }>({ docs: true, interview: true, live: false });

  useEffect(() => {
    fetchRecommendations();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchRecommendations is stable
  }, [groupId]);

  const fetchRecommendations = async () => {
    setError(null);
    try {
      const response = await authFetch(`/api/journey/${groupId}/recommendations`);
      if (response.ok) {
        const data = await response.json();
        setRecommendations(data.recommendations || []);
      } else {
        console.error('Failed to fetch recommendations:', response.status, response.statusText);
        setError(`The server returned an error (${response.status}). Your recommendations are safe — try again in a moment.`);
      }
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch recommendations:', err);
      setError('Recommendations could not be loaded — check your connection and try again.');
      setLoading(false);
    }
  };

  // Filter recommendations based on checkbox states
  const filteredRecs = recommendations.filter((rec) => {
    // Category filter
    const catMatch =
      (filterCategory.critical && rec.category === 'critical_issue') ||
      (filterCategory.optimize && rec.category === 'optimization_opportunity') ||
      (filterCategory.best_practice && rec.category === 'best_practice') ||
      (filterCategory.future && rec.category === 'future_enhancement');

    if (!catMatch) return false;

    // Priority filter
    const priMatch =
      (filterPriority.critical && rec.priority === 'critical') ||
      (filterPriority.high && rec.priority === 'high') ||
      (filterPriority.medium && rec.priority === 'medium') ||
      (filterPriority.low && rec.priority === 'low');

    if (!priMatch) return false;

    // Status filter
    const statMatch =
      (filterStatus.new && rec.status === 'new') ||
      (filterStatus.approved && rec.status === 'approved') ||
      (filterStatus.implemented && rec.status === 'implemented');

    if (!statMatch) return false;

    return true;
  });

  const selectedRec = selectedId ? recommendations.find(r => r.id === selectedId) : null;

  if (loading) {
    return <div className="rec-loading-state"><div className="rec-loading-spinner" /><p className="rec-loading-text">Loading recommendations...</p></div>;
  }

  if (error) {
    return (
      <div className="rec-empty-state" role="alert">
        <h3 className="rec-empty-title">Couldn't load recommendations</h3>
        <p className="rec-empty-description">{error}</p>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => {
            setLoading(true);
            fetchRecommendations();
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // Handler for Calculate Business Value button
  const handleCalculateBusinessValue = () => {
    navigate(`/journey/${groupId}/calculator`);
  };

  // EXACT THREE-PANEL LAYOUT from design doc lines 569-600
  return (
    <>
      {/* Business Value Calculator CTA (only show if recommendations > 0) */}
      {recommendations.length > 0 && (
        <div className="business-value-cta-card">
          <div className="business-value-cta-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" fill="url(#gradient1)" />
              <path d="M12 6v6l4 2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <defs>
                <linearGradient id="gradient1" x1="2" y1="2" x2="22" y2="22">
                  <stop offset="0%" stopColor="#0094ab" />
                  <stop offset="100%" stopColor="#004772" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <div className="business-value-cta-content">
            <h3 className="business-value-cta-title">Ready to Calculate Business Value?</h3>
            <p className="business-value-cta-description">
              You've identified <strong>{recommendations.length} recommendations</strong> across {new Set(recommendations.map(r => r.station_id)).size} stations.
              Calculate ROI, NPV, IRR, and payback period for your FAIR implementation.
            </p>
          </div>
          <button className="business-value-cta-button" onClick={handleCalculateBusinessValue}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="20" x2="18" y2="10" />
              <line x1="12" y1="20" x2="12" y2="4" />
              <line x1="6" y1="20" x2="6" y2="14" />
            </svg>
            Calculate Business Value
          </button>
        </div>
      )}

      <div className="rec-planner-layout">
        {/* LEFT PANEL: FILTERS (280px) */}
      <div className="rec-filters-panel">
        <div className="rec-filters-header">
          <h3>Filters</h3>
        </div>
        <div className="rec-filters-content">
          {/* Category Filter */}
          <div className="rec-filter-group">
            <label className="rec-filter-group-label">Category</label>
            <div className="rec-filter-options">
              <label className="filter-checkbox-label">
                <input
                  type="checkbox"
                  className="filter-checkbox"
                  checked={filterCategory.critical}
                  onChange={(e) => setFilterCategory({ ...filterCategory, critical: e.target.checked })}
                />
                <span className="filter-checkbox-text">☑ Critical</span>
              </label>
              <label className="filter-checkbox-label">
                <input
                  type="checkbox"
                  className="filter-checkbox"
                  checked={filterCategory.optimize}
                  onChange={(e) => setFilterCategory({ ...filterCategory, optimize: e.target.checked })}
                />
                <span className="filter-checkbox-text">☑ Optimize</span>
              </label>
              <label className="filter-checkbox-label">
                <input
                  type="checkbox"
                  className="filter-checkbox"
                  checked={filterCategory.best_practice}
                  onChange={(e) => setFilterCategory({ ...filterCategory, best_practice: e.target.checked })}
                />
                <span className="filter-checkbox-text">□ Best Prc</span>
              </label>
              <label className="filter-checkbox-label">
                <input
                  type="checkbox"
                  className="filter-checkbox"
                  checked={filterCategory.future}
                  onChange={(e) => setFilterCategory({ ...filterCategory, future: e.target.checked })}
                />
                <span className="filter-checkbox-text">□ Future</span>
              </label>
            </div>
          </div>

          {/* Priority Filter */}
          <div className="rec-filter-group">
            <label className="rec-filter-group-label">Priority</label>
            <div className="rec-filter-options">
              <label className="filter-checkbox-label">
                <input type="checkbox" className="filter-checkbox" checked={filterPriority.critical} onChange={(e) => setFilterPriority({ ...filterPriority, critical: e.target.checked })} />
                <span className="filter-checkbox-text">☑ Critical</span>
              </label>
              <label className="filter-checkbox-label">
                <input type="checkbox" className="filter-checkbox" checked={filterPriority.high} onChange={(e) => setFilterPriority({ ...filterPriority, high: e.target.checked })} />
                <span className="filter-checkbox-text">☑ High</span>
              </label>
              <label className="filter-checkbox-label">
                <input type="checkbox" className="filter-checkbox" checked={filterPriority.medium} onChange={(e) => setFilterPriority({ ...filterPriority, medium: e.target.checked })} />
                <span className="filter-checkbox-text">□ Medium</span>
              </label>
              <label className="filter-checkbox-label">
                <input type="checkbox" className="filter-checkbox" checked={filterPriority.low} onChange={(e) => setFilterPriority({ ...filterPriority, low: e.target.checked })} />
                <span className="filter-checkbox-text">□ Low</span>
              </label>
            </div>
          </div>

          {/* Status Filter */}
          <div className="rec-filter-group">
            <label className="rec-filter-group-label">Status</label>
            <div className="rec-filter-options">
              <label className="filter-checkbox-label">
                <input type="checkbox" className="filter-checkbox" checked={filterStatus.new} onChange={(e) => setFilterStatus({ ...filterStatus, new: e.target.checked })} />
                <span className="filter-checkbox-text">☑ New</span>
              </label>
              <label className="filter-checkbox-label">
                <input type="checkbox" className="filter-checkbox" checked={filterStatus.approved} onChange={(e) => setFilterStatus({ ...filterStatus, approved: e.target.checked })} />
                <span className="filter-checkbox-text">□ Approved</span>
              </label>
              <label className="filter-checkbox-label">
                <input type="checkbox" className="filter-checkbox" checked={filterStatus.implemented} onChange={(e) => setFilterStatus({ ...filterStatus, implemented: e.target.checked })} />
                <span className="filter-checkbox-text">□ Implemtd</span>
              </label>
            </div>
          </div>

          {/* Station Filter */}
          <div className="rec-filter-group">
            <label className="rec-filter-group-label">Station</label>
            <div className="rec-filter-options">
              <label className="filter-checkbox-label">
                <input type="checkbox" className="filter-checkbox" checked={filterStation.docs} onChange={(e) => setFilterStation({ ...filterStation, docs: e.target.checked })} />
                <span className="filter-checkbox-text">☑ Docs</span>
              </label>
              <label className="filter-checkbox-label">
                <input type="checkbox" className="filter-checkbox" checked={filterStation.interview} onChange={(e) => setFilterStation({ ...filterStation, interview: e.target.checked })} />
                <span className="filter-checkbox-text">☑ Intvw</span>
              </label>
              <label className="filter-checkbox-label">
                <input type="checkbox" className="filter-checkbox" checked={filterStation.live} onChange={(e) => setFilterStation({ ...filterStation, live: e.target.checked })} />
                <span className="filter-checkbox-text">□ Live</span>
              </label>
            </div>
          </div>

          <button className="btn btn-secondary btn-sm" onClick={() => {
            setFilterCategory({ critical: false, optimize: false, best_practice: false, future: false });
            setFilterPriority({ critical: false, high: false, medium: false, low: false });
            setFilterStatus({ new: false, approved: false, implemented: false });
          }}>
            [Clear]
          </button>
        </div>
      </div>

      {/* CENTER PANEL: RECOMMENDATIONS GRID (Flex 1) */}
      <div className="rec-grid-panel">
        {filteredRecs.map((rec) => (
          <div
            key={rec.id}
            className={`rec-card category-${rec.category.replace('_', '-')} ${selectedId === rec.id ? 'selected' : ''}`}
            onClick={() => setSelectedId(rec.id)}
          >
            <div className="rec-card-header">
              <div
                className="rec-category-badge"
                role="img"
                aria-label={CATEGORY_LABELS[rec.category]}
                title={CATEGORY_LABELS[rec.category]}
              >
                {rec.category === 'critical_issue' ? '🔴' : rec.category === 'optimization_opportunity' ? '🟡' : rec.category === 'best_practice' ? '🔵' : '⚪'}
              </div>
              <div className="rec-card-title-section">
                <h4 className="rec-card-title">{rec.title}</h4>
                <div className="rec-card-badges">
                  <span className={`badge-priority ${rec.priority}`}>{rec.priority.toUpperCase()}</span>
                  {rec.impact_score >= 7 && rec.effort_level === 'low' && (
                    <span className="badge-quick-win">⚡ QUICK WIN</span>
                  )}
                </div>
              </div>
            </div>
            <p className="rec-card-description">{rec.description}</p>
            <div className="rec-card-metrics">
              <div className="rec-metric">
                <span className="rec-metric-label">Impact</span>
                <span className="rec-metric-value impact">{rec.impact_score}</span>
              </div>
              <div className="rec-metric">
                <span className="rec-metric-label">Effort</span>
                <span className="rec-metric-value">{rec.effort_level}</span>
              </div>
              {rec.estimated_cost_min && (
                <div className="rec-metric">
                  <span className="rec-metric-label">Cost</span>
                  <span className="rec-metric-value">${rec.estimated_cost_min / 1000}K</span>
                </div>
              )}
            </div>
            <div className="rec-card-footer">
              <span className="rec-station-tag">📍 {rec.station_id}</span>
              <span className={`rec-status-badge status-${rec.status}`}>{rec.status}</span>
            </div>
          </div>
        ))}

        {filteredRecs.length === 0 && (
          <div className="rec-empty-state">
            <div className="rec-empty-icon">📋</div>
            <h3 className="rec-empty-title">No recommendations match filters</h3>
            <p className="rec-empty-description">Try adjusting your filters</p>
          </div>
        )}
      </div>

      {/* RIGHT PANEL: DETAIL PANEL (480px) */}
      {selectedRec && (
        <div className="rec-detail-panel">
          <div className="rec-detail-header">
            <button className="rec-detail-close" onClick={() => setSelectedId(null)}>×</button>
          </div>
          <div className="rec-detail-content">
            <h2 className="rec-detail-title">{selectedRec.title}</h2>
            <div className="rec-detail-badges">
              <span className={`badge-priority ${selectedRec.priority}`}>{selectedRec.priority}</span>
              <span className={`rec-status-badge status-${selectedRec.status}`}>{selectedRec.status}</span>
            </div>

            <div className="rec-detail-description">{selectedRec.description}</div>

            <div className="rec-detail-section">
              <h4 className="rec-detail-section-title">Metrics</h4>
              <div className="rec-detail-metrics-grid">
                <div className="rec-detail-metric-card">
                  <span className="rec-metric-label">Impact</span>
                  <span className="rec-metric-value">{selectedRec.impact_score}/10</span>
                </div>
                <div className="rec-detail-metric-card">
                  <span className="rec-metric-label">Effort</span>
                  <span className="rec-metric-value">{selectedRec.effort_level}</span>
                </div>
              </div>
            </div>

            <div className="rec-detail-section">
              <h4 className="rec-detail-section-title">FAIR Pillars</h4>
              <div className="rec-pillar-tags">
                {selectedRec.fair_pillars.map((pillar) => (
                  <span key={pillar} className={`rec-pillar-tag pillar-${pillar}`}>{pillar}</span>
                ))}
              </div>
            </div>

            {selectedRec.evidence && selectedRec.evidence.length > 0 && (
              <div className="rec-detail-section">
                <h4 className="rec-detail-section-title">Evidence</h4>
                <div className="rec-evidence-list">
                  {selectedRec.evidence.map((ev, idx) => (
                    <div key={idx} className="rec-evidence-card">
                      <div className="rec-evidence-source">{ev.source_name}</div>
                      <div className="rec-evidence-text">"{ev.excerpt}"</div>
                      <div className="rec-evidence-link">{ev.location}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </>
  );
};
